import { Box, Skeleton, Stack, useTheme } from "@vapor/react-material";
import { ExtendedTypography } from "@vapor/react-extended";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface InfoBoxProps {
    title?: string | React.ReactNode;
    description?: string | React.ReactNode;
    loading?: boolean;
    loadingRows?: number;
    loadingRowsWidth?: number;
    variant?: "standard" | "ghost";
    icon?: IconDefinition;
    sx?: any;
}

export const InfoBox = ({
    title,
    description,
    loading,
    loadingRows = 3,
    loadingRowsWidth = 680,
    variant,
    icon,
    sx
}: InfoBoxProps) => {
    const {
        palette: {
            primary: {
                background: WhiteSmoke,
                textSubduedColor: Cadet,
                textTitleColor: DarkImperialBlue,
                textHintColor: ColumbiaBlue,
                textDefaultColor: YankeesBlue
            }
        }
    } = useTheme();

    return (
        <Box
            sx={{
                display: "block",
                background: variant === "ghost" ? "transparent" : WhiteSmoke,
                border: variant === "ghost" ? `1px solid ${ColumbiaBlue}` : "none",
                padding: "16px",
                width: "fit-content",
                ...sx
            }}
        >
            {title && !loading && (
                <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <Box>
                        <FontAwesomeIcon icon={icon || faInfoCircle} color={DarkImperialBlue} />
                    </Box>
                    <Box>
                        <ExtendedTypography variant="body700" gutterBottom color={YankeesBlue}>
                            {title}
                        </ExtendedTypography>
                    </Box>
                </Stack>
            )}
            {loading && title && (
                <Stack direction={"row"} spacing={1}>
                    <Box>
                        <FontAwesomeIcon icon={faInfoCircle} color={DarkImperialBlue} />
                    </Box>
                    <Box>
                        <Skeleton width={loadingRowsWidth} animation="wave" variant="text" />
                    </Box>
                </Stack>
            )}
            {description && !loading && (
                <Stack direction={"row"} spacing={1}>
                    {!title && (
                        <Box>
                            <FontAwesomeIcon icon={faInfoCircle} color={DarkImperialBlue} />
                        </Box>
                    )}
                    <Box sx={{ marginTop: ".5rem" }}>
                        <ExtendedTypography variant="body" gutterBottom color={Cadet}>
                            {description}
                        </ExtendedTypography>
                    </Box>
                </Stack>
            )}
            {loading && description && (
                <Stack direction={"row"} spacing={1}>
                    {!title && (
                        <Box>
                            <FontAwesomeIcon icon={faInfoCircle} color={DarkImperialBlue} />
                        </Box>
                    )}
                    <Box sx={{ marginTop: ".5rem" }}>
                        {[...Array(loadingRows || 2)].map((_, i) => (
                            <Skeleton
                                key={i}
                                width={`${100 - i * Math.random() * 10}%`}
                                animation="wave"
                                variant="text"
                            />
                        ))}
                    </Box>
                </Stack>
            )}
        </Box>
    );
};
